import { useMemo } from 'react';
import { DescriptorType, FieldListObject } from '@fabric-ui/components';
import { CALCULATED_DIMENSIONS_GROUP } from '@views/CalculateDimensions/utils';
import { useAvContext } from '../context/AvContextProvider';
import { parseCalcDimType } from '../context/useCalcDims';

/** Dedup identity for interactive filter fields - two options are the same field when this matches. */
export const getInteractiveFieldKey = ({ mainProjId, group, name }: Partial<FieldListObject>) => `${mainProjId?.name} - ${group} - ${name}`;

const useGlobalInteractiveFilters = ({ projectionNames }: { projectionNames?: ReadonlySet<string> } = {}): FieldListObject[] => {
  const {
    accountEntities: { aggProjs },
    calculatedDimensions,
  } = useAvContext();
  return useMemo(() => {
    const projections = projectionNames
      ? Object.entries(aggProjs)
          .filter(([projectionName]) => projectionNames.has(projectionName))
          .map(([, projection]) => projection)
      : Object.values(aggProjs);
    const aggFields = projections.reduce(
      (allFieldsToFilterBy, { fieldList }) => ({
        ...allFieldsToFilterBy,
        ...(fieldList?.INTERACTIVE ?? []).reduce((projFieldsToFilterBy, field) => {
          const key = getInteractiveFieldKey(field);
          return field.visibilityConfig.config.hidden
            ? projFieldsToFilterBy
            : {
                ...projFieldsToFilterBy,
                [key]: {
                  ...field,
                  otherAliases: [...(allFieldsToFilterBy[key]?.otherAliases ?? []), field.value],
                },
              };
        }, {}),
      }),
      {}
    );

    const calcFields = calculatedDimensions.all.reduce((acc, calcDim) => {
      const matchingProjection = calcDim.context?.find(({ name }) => projectionNames?.has(name))?.name;
      const key = `calculated - ${calcDim.systemName}`;
      const type = parseCalcDimType(calcDim.type);
      return calcDim.hidden || (projectionNames && !matchingProjection)
        ? acc
        : {
            ...acc,
            [key]: {
              value: calcDim.systemName,
              title: calcDim.displayName,
              name: calcDim.systemName,
              group: CALCULATED_DIMENSIONS_GROUP,
              type: type ? (DescriptorType[type] ?? type) : type,
              mainProjId: { name: matchingProjection ?? 'calculated' },
              otherAliases: [calcDim.systemName],
            },
          };
    }, {});

    return [...Object.values(aggFields), ...Object.values(calcFields)] as FieldListObject[];
  }, [aggProjs, calculatedDimensions.all, projectionNames]);
};

export default useGlobalInteractiveFilters;
